import React, { useState } from 'react';
import Button from './Button';
import { useNavigate } from 'react-router-dom';

interface CardProps {
  title: string;
  creator: string;
  price: string | number;
  isLiked?: boolean;
  imageUrl?: string;
  rating?: number;
  sales?: number;
  badges?: string[];
  isNew?: boolean;
  discount?: number;
  id?: number;
}

const Card: React.FC<CardProps> = ({
  title,
  creator,
  price,
  isLiked = false,
  imageUrl,
  rating = 4.5,
  sales = Math.floor(Math.random() * 500) + 100,
  badges = [],
  isNew = false,
  discount = 0,
  id = 1
}) => {
  const [liked, setLiked] = useState(isLiked);
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  const handleLike = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLiked(!liked);
  };

  const handleCardClick = () => {
    navigate(`/product/${id}`);
  };

  const originalPrice = typeof price === 'number'
    ? price / (1 - discount / 100)
    : parseFloat(price as string) / (1 - discount / 100);

  return (
    <div
      className="card group cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleCardClick}
    >
      <div className="card-image overflow-hidden relative">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#2A2633] to-[#1F1A27]">
            <div className="flex items-center justify-center h-full text-[#7F56D9] opacity-50">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM13.96 12.29L11.21 15.83L9.25 13.47L6.5 17H17.5L13.96 12.29Z" fill="currentColor"/>
              </svg>
            </div>
          </div>
        )}

        {/* Badges */}
        {badges.length > 0 && (
          <div className="absolute top-2 left-2 flex flex-wrap gap-1 z-10">
            {badges.map((badge, index) => (
              <span
                key={index}
                className={`text-xs font-medium py-0.5 px-2 rounded-full ${
                  index === 0 ? 'bg-[#7F56D9] text-white' : 'bg-[#2A2633] text-white'
                }`}
              >
                {badge}
              </span>
            ))}
          </div>
        )}

        {/* New badge */}
        {isNew && (
          <div className="absolute top-2 right-2 bg-[#F04438] text-white text-xs font-medium py-0.5 px-2 rounded-full z-10">
            New
          </div>
        )}

        {/* Discount badge */}
        {discount > 0 && (
          <div className="absolute top-2 right-2 bg-[#F59E0B] text-white text-xs font-medium py-0.5 px-2 rounded-full z-10">
            -{discount}%
          </div>
        )}

        {hovered && (
          <div className={`absolute top-2 right-2 ${(isNew || discount > 0) ? 'right-14' : ''}`}>
            <Button
              variant="secondary"
              size="sm"
              className="!p-1 rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/product/${id}`, '_blank');
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor"/>
              </svg>
            </Button>
          </div>
        )}

        {hovered && (
          <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
            <div className="flex items-center gap-2">
              <div className="text-xs text-yellow-400 flex items-center">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z"/>
                </svg>
                <span className="ml-1">{rating.toFixed(1)}</span>
              </div>
              <div className="text-xs text-gray-300">{sales} sales</div>
            </div>
          </div>
        )}
      </div>

      <div className="card-body">
        <div className="flex justify-between">
          <h3 className="font-medium truncate text-sm md:text-base pr-2 group-hover:text-[#7F56D9] transition-colors">
            {title}
          </h3>
          <button
            className="w-6 h-6 flex-shrink-0 flex items-center justify-center transition-transform hover:scale-110"
            onClick={handleLike}
          >
            {liked ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z"
                  fill="#FF3C8E"
                  stroke="#FFAACE" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-gray-400 hover:text-[#FF3C8E]" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" strokeWidth="1.5" />
              </svg>
            )}
          </button>
        </div>

        <div className="flex justify-between mt-2 items-center">
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full bg-[#F670C7] mr-1.5"></div>
            <p className="text-xs md:text-sm text-[#A3A3A3]">by {creator}</p>
          </div>
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-1 text-[#F7931A] font-medium">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="mr-1" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {discount > 0 && (
                <span className="text-xs text-[#A3A3A3] line-through">{Math.round(originalPrice)}</span>
              )}
              <span className="text-sm md:text-base">{typeof price === 'number' ? Math.round(price) : Math.round(parseFloat(price as string))}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Card;
